import React from 'react';
import { Paper, Typography, Box } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const item = payload[0]?.payload;
    return (
      <Paper
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: '1px solid #DADCE0',
          boxShadow: '0 1px 3px rgba(60,64,67,0.3)',
        }}
        elevation={0}
      >
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#202124' }} gutterBottom>
          {label}
        </Typography>
        <Typography variant="caption" display="block" sx={{ color: '#5F6368' }}>
          Total Files: {item?.count || 0}
        </Typography>
        <Typography variant="caption" display="block" sx={{ color: '#14B8A6' }}>
          Approved: {item?.approved || 0}
        </Typography>
        <Typography variant="caption" display="block" sx={{ color: '#0EA5E9' }}>
          Disbursed: {item?.disbursed || 0}
        </Typography>
        {item?.totalAmount > 0 && (
          <Typography variant="caption" display="block" sx={{ color: '#202124', mt: 0.5 }}>
            Total Amount: ₹{(item.totalAmount / 100000).toFixed(1)}L
          </Typography>
        )}
      </Paper>
    );
  }
  return null;
};

const BankWiseChart = ({ data = [] }) => {
  const safeData = Array.isArray(data) ? data : [];
  const chartData = safeData.map((item) => ({
    name: item._id,
    count: item.count,
    approved: item.approved || 0,
    disbursed: item.disbursed || 0,
    totalAmount: item.totalAmount || 0,
  }));

  if (chartData.length === 0) {
    return (
      <Paper
        sx={{
          p: 3,
          height: '100%',
          borderRadius: 3,
          border: '1px solid #E2E8F0',
          boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
          transition: 'all 0.25s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(15, 23, 42, 0.08)',
          },
        }}
        elevation={0}
      >
        <Typography variant="h6" sx={{ fontWeight: 500, color: '#202124', fontSize: '1rem' }} gutterBottom>
          Bank-wise Distribution
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 300,
          }}
        >
          <Typography variant="body2" sx={{ color: '#5F6368' }}>
            No data available
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 3,
        height: '100%',
        borderRadius: 2,
        border: '1px solid #DADCE0',
      }}
      elevation={0}
    >
      <Typography variant="h6" sx={{ fontWeight: 500, color: '#202124', fontSize: '1rem' }} gutterBottom>
        Bank-wise Distribution
      </Typography>
      <ResponsiveContainer width="100%" height={350}>
        <BarChart
          data={chartData}
          margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 12, fill: '#5F6368' }}
            angle={-30}
            textAnchor="end"
            height={60}
            axisLine={{ stroke: '#E2E8F0' }}
            tickLine={false}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 12, fill: '#5F6368' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(44, 123, 229, 0.04)' }} />
          <Legend
            wrapperStyle={{ fontSize: '0.75rem', color: '#5F6368' }}
          />
          <Bar
            dataKey="count"
            name="Total Files"
            fill="#1A73E8"
            radius={[6, 6, 0, 0]}
          />
          <Bar
            dataKey="approved"
            name="Approved"
            fill="#1E8E3E"
            radius={[6, 6, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default BankWiseChart;
